import { useState } from 'react';
import { useNotify } from '../../../components/notifications';
import { AiCombo } from '../../../components/AiCombo';
import { useConfirm } from '../../../components/confirm';
import { useDepartments, useSaveDepartment, departmentParentOptions, errMessage, type Department } from './systemMasters';
import { DepartmentEmployeesPanel } from './DepartmentEmployeesPanel';
import { DepartmentChildPanel } from './DepartmentChildPanel';
import './departmentDetail.css';

/**
 * The full Department detail form as a stacked dialog (Department + Parent + Sort,
 * with the Employees tab) — opened from a grid/picker edit pencil, mirroring the
 * LIMS "{name} - Department" form: fields on the left, Employees | Child Department tabs below,
 * Maximize in the header (Child Department tab and the legacy layout restored 2026-10-12).
 */
export function DepartmentDetailDialog({ department, onClose }: { department: Department; onClose: () => void }) {
  const notify = useNotify();
  const { alert } = useConfirm();
  const save = useSaveDepartment();
  const { data: all = [] } = useDepartments();

  const [name, setName] = useState(department.name ?? '');
  const [parentId, setParentId] = useState(department.parentId ?? '');
  const [sort, setSort] = useState(department.sort != null ? String(department.sort) : '');
  const [err, setErr] = useState('');
  const [tab, setTab] = useState<'employees' | 'children'>('children');   // G5 opens this popup on Child Department
  const [maxed, setMaxed] = useState(false);

  const submit = async () => {
    if (!name.trim()) { setErr('Department must not be empty'); await alert({ title: 'Validation', message: 'Department must not be empty', tone: 'error' }); return; }
    setErr('');
    try {
      await save.mutateAsync({ id: department.id, name: name.trim(), sort: sort.trim() === '' ? null : Number(sort), parentId: parentId || null });
      await alert({ title: 'Success', message: 'Saved successfully.', tone: 'success' });
      onClose();
    } catch (e) { notify.error(errMessage(e)); }
  };

  return (
    <div className="acc-overlay" style={{ zIndex: 1250 }} onMouseDown={(e) => { e.stopPropagation(); onClose(); }}>
      <div className={`acc-dialog acc-dialog-lg dep-dlg acc-dialog--accent${maxed ? ' acc-dialog-max' : ''}`} onMouseDown={(e) => e.stopPropagation()}>
        <div className="acc-dialog-hdr">
          <span>{`${name || 'Department'} - Department`}</span>
          <span className="acc-dialog-acts">
            <i className={`ti ${maxed ? 'ti-minimize' : 'ti-maximize'} acc-dialog-x`} role="button" title={maxed ? 'Restore' : 'Maximize'} onClick={() => setMaxed((m) => !m)} />
            <i className="ti ti-x acc-dialog-x" role="button" title="Close" onClick={onClose} />
          </span>
        </div>
        <div className="acc-dialog-body">
          <div className="dep-dlg-fields">
            <div className="acc-field-h">
              <label className="acc-label acc-label-req">Department<span className="req">*</span></label>
              <div className="acc-fh-control">
                <input className="acc-input" autoFocus value={name} onChange={(e) => { setName(e.target.value); if (err) setErr(''); }} onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }} />
                {err && <div className="acc-err">{err}</div>}
              </div>
            </div>
            <div className="acc-field-h">
              <label className="acc-label">Parent</label>
              <div className="acc-fh-control">
                <AiCombo value={parentId} options={departmentParentOptions(all)}
                         onChange={(v) => setParentId(v ?? '')} />
              </div>
            </div>
            <div className="acc-field-h">
              <label className="acc-label">Sort</label>
              <div className="acc-fh-control"><input className="acc-input" type="number" value={sort} placeholder="(auto)" onChange={(e) => setSort(e.target.value)} /></div>
            </div>
          </div>
          <div className="acc-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === 'employees'} className={`acc-tab${tab === 'employees' ? ' active' : ''}`} onClick={() => setTab('employees')}><i className="ti ti-users" /> Employees</button>
            <button type="button" role="tab" aria-selected={tab === 'children'} className={`acc-tab${tab === 'children' ? ' active' : ''}`} onClick={() => setTab('children')}><i className="ti ti-sitemap" /> Child Department</button>
          </div>
          {tab === 'employees' ? <DepartmentEmployeesPanel departmentId={department.id} /> : <DepartmentChildPanel departmentId={department.id} editRight />}
        </div>
        <div className="acc-dialog-ftr">
          <button className="ca-btn ca-btn-solid" onClick={() => void submit()} disabled={save.isPending}><span className="tb-i">💾</span> {save.isPending ? 'Saving…' : 'OK'}</button>
          <button className="ca-btn" onClick={onClose}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
        </div>
      </div>
    </div>
  );
}
